<script>
import { formatDateUTC, calcAgeFromUTC } from "@/utils/date";
import { ALLOGGIATI_STATI } from "@/utils/alloggiatiStati.js";

export default {
  name: "IndividualCheckin",
  props: {
    mostrar:       { type: Object,   required: true },
    dias:          { type: Function, required: true },
    edad:          { type: Function, required: true },
    isVisibleData: { type: Boolean,  required: true },
    isItaly:       { type: Boolean,  default: false },
    validateField: { type: Function, required: true },
    property:      { type: Object,   required: true },
    // Autoridad de registro: { key, label, flag }. Por defecto SES (compat).
    authority:     { type: Object,   default: () => ({ key: "ses_hospedaje", label: "SES Hospedaje", flag: "🇪🇸" }) },
  },
  data() {
    return {
      previewOpen:  false,
      previewUrl:   null,
      previewTitle: "",
    };
  },
  computed: {
    minor() {
      return this.mostrar?.cliente?.minor_info || null;
    },
    hasMinorItem() {
      return !!(
        this.minor &&
        (this.minor.minor_document_photo_url || this.minor.minor_authorization_photo_url)
      );
    },
    relationship() {
      return this.mostrar?.cliente?.relationship || "—";
    },

    // ── Nombre legible de la nacionalidad ──────────────────────────────────
    nationalityLabel() {
      const c = this.mostrar?.cliente || {};

      // 1) Para propiedades de Italia: resolver desde ALLOGGIATI_STATI
      if (this.isItaly) {
        const iso3 = String(c.pais_code || "").trim().toUpperCase();
        if (iso3) {
          const entry = ALLOGGIATI_STATI.find(
            (s) => s.iso3 === iso3 || s.code === iso3
          );
          if (entry) return entry.es || entry.nome || iso3;
        }
        return iso3 || "—";
      }

      // 2) Para Colombia / España / genérico: usar el nombre guardado
      const name = String(c.nacionalidad || c.pais_name_es || "").trim();
      if (name) return name;

      // 3) Fallback al código
      return c.pais_code || "—";
    },

    // ── País nacimiento Italia (nombre legible) ─────────────────────────────
    paisNascitaLabel() {
      const code = this.mostrar?.cliente?.paese_nascita_code;
      if (!code) {
        // Fallback: intentar con pais_code (ISO3)
        const iso3 = String(this.mostrar?.cliente?.pais_code || "").trim().toUpperCase();
        if (iso3) {
          const entry = ALLOGGIATI_STATI.find((s) => s.iso3 === iso3);
          return entry ? (entry.es || entry.nome) : iso3 || "—";
        }
        return "—";
      }
      const entry = ALLOGGIATI_STATI.find((s) => s.code === code);
      return entry ? (entry.es || entry.nome) : code;
    },

    // ── Lugar expedición Italia (nombre legible) ────────────────────────────
    luogoRilascioLabel() {
      const raw = this.mostrar?.cliente?.luogo_rilascio_doc;
      if (!raw) return null;
      const iso3 = String(raw).trim().toUpperCase();
      const entry = ALLOGGIATI_STATI.find((s) => s.iso3 === iso3 || s.code === raw);
      return entry ? (entry.es || entry.nome) : raw;
    },

  },
  methods: {
    formatDateUTC,
    calcAgeFromUTC,

    openEditDrawer() {
      this.$emit("edit-checkin", { item: this.mostrar });
    },

    docTypeLabel(type) {
      const t = (type || "").toUpperCase().trim();
      const map = {
        P: "passport", PASS: "passport", PPT: "passport",
        PASOR: "passportOrdinary", PASSE: "passportService",
        PASDI: "passportDiplomatic",
        I: "idCard", ID: "idCard",
        IM: "nie", IDEN: "idCard", IDENT: "idCard",
        IDELE: "idCardElectronic", CIDIP: "idDiplomatic",
        CC: "citizenshipCard", CE: "foreignerCard",
        PATE: "drivingLicense", PATEN: "drivingLicense",
        PATNA: "nauticalLicense",
        PEP: "specialResidencePermit", DE: "foreignerDocument",
        DEXT: "foreignDocument", CD: "diplomaticCard",
        RIFUG: "refugeeTravelDocument", OTRO: "otherDocument",
      };
      const key = map[t];
      return key ? this.$t(`checkinList.individual.docTypes.${key}`) : (type || "—");
    },

    openPreview(url, title) {
      if (!url) return;
      this.previewUrl   = url;
      this.previewTitle = title || this.$t("checkinList.individual.previewFallbackTitle");
      this.previewOpen  = true;
    },
    closePreview() {
      this.previewOpen  = false;
      this.previewUrl   = null;
      this.previewTitle = "";
    },
  },
};
</script>

<template>
  <div class="wk-single">

    <!-- ─── BLOQUE PRINCIPAL ──────────────────────────────────────────────── -->
    <div class="wk-single__section">
      <div class="wk-single__title">{{ $t('checkinList.individual.title') }}</div>
      <div class="wk-single__name">{{ mostrar.nombre }} {{ mostrar.apellido }}</div>

      <div class="wk-card">
        <!-- Contacto -->
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.phone') }}</div>
          <div class="wk-kv__v">{{ mostrar?.cliente?.whatsapp || "—" }}</div>
        </div>
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.email') }}</div>
          <div class="wk-kv__v">{{ mostrar?.cliente?.email || $t('checkinList.individual.notAvailable') }}</div>
        </div>

        <!-- Fechas -->
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.lodging') }}</div>
          <div class="wk-kv__v">
            {{ formatDateUTC(mostrar?.cliente?.fecha_desde_hospedaje) }}
            <span class="wk-arrow">→</span>
            {{ formatDateUTC(mostrar?.cliente?.fecha_hospedaje) }}
          </div>
        </div>

        <!-- Documento -->
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.docType') }}</div>
          <div class="wk-kv__v">{{ docTypeLabel(mostrar?.type_documento || mostrar?.cliente?.type_documento) }}</div>
        </div>
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.docNumber') }}</div>
          <div class="wk-kv__v">{{ mostrar.nu_documento || "—" }}</div>
        </div>

        <!-- Soporte: solo España -->
        <div class="wk-kv__row" v-if="isVisibleData">
          <div class="wk-kv__k">{{ $t('checkinList.individual.supportNumber') }}</div>
          <div class="wk-kv__v">{{ validateField(mostrar?.cliente?.document_support) }}</div>
        </div>

        <!-- Fotos: solo Colombia (ni España ni Italia) -->
        <div class="wk-media-stack" v-if="(mostrar?.identity_photos || minor) && !isItaly && !isVisibleData">

          <!-- Verificación de identidad -->
          <div class="wk-media-section" v-if="mostrar?.identity_photos">
            <div class="wk-media-head">
              <div class="wk-media-title">
                {{ $t('checkinList.individual.identityVerification') }}
                <span class="wk-media-meta" v-if="mostrar.identity_photos.similarity != null">
                  ({{ Number(mostrar.identity_photos.similarity).toFixed(2) }}%)
                </span>
              </div>
            </div>
            <div class="wk-media-list">
              <div class="wk-media-item">
                <button type="button" class="wk-media-main" @click="openPreview(mostrar.identity_photos.dni_front_url, $t('checkinList.individual.document'))">
                  <img class="wk-media-thumb" :src="mostrar.identity_photos.dni_front_url" :alt="$t('checkinList.individual.document')" />
                  <div class="wk-media-body">
                    <div class="wk-media-name">{{ $t('checkinList.individual.document') }}</div>
                    <div class="wk-media-sub">{{ $t('checkinList.individual.tapToEnlarge') }}</div>
                  </div>
                </button>
                <div class="wk-media-actions">
                  <button type="button" class="wk-icon-btn" @click="openPreview(mostrar.identity_photos.dni_front_url, $t('checkinList.individual.document'))" :title="$t('checkinList.individual.view')"><i class="fa-regular fa-eye"></i></button>
                  <a class="wk-icon-btn" :href="mostrar.identity_photos.dni_front_download_url" target="_blank" rel="noopener" :title="$t('checkinList.individual.download')"><i class="fa-solid fa-download"></i></a>
                </div>
              </div>
              <div class="wk-media-item">
                <button type="button" class="wk-media-main" @click="openPreview(mostrar.identity_photos.selfie_url, $t('checkinList.individual.selfie'))">
                  <img class="wk-media-thumb" :src="mostrar.identity_photos.selfie_url" :alt="$t('checkinList.individual.selfie')" />
                  <div class="wk-media-body">
                    <div class="wk-media-name">{{ $t('checkinList.individual.selfie') }}</div>
                    <div class="wk-media-sub">{{ $t('checkinList.individual.tapToEnlarge') }}</div>
                  </div>
                </button>
                <div class="wk-media-actions">
                  <button type="button" class="wk-icon-btn" @click="openPreview(mostrar.identity_photos.selfie_url, $t('checkinList.individual.selfie'))" :title="$t('checkinList.individual.view')"><i class="fa-regular fa-eye"></i></button>
                  <a class="wk-icon-btn" :href="mostrar.identity_photos.selfie_download_url" target="_blank" rel="noopener" :title="$t('checkinList.individual.download')"><i class="fa-solid fa-download"></i></a>
                </div>
              </div>
            </div>
          </div>

          <!-- Menor de edad -->
          <div class="wk-media-section" v-if="minor">
            <div class="wk-media-head wk-media-head--split">
              <div class="wk-media-title">{{ $t('checkinList.individual.minorData') }}</div>
              <div class="wk-media-right">
                <span class="wk-media-k">{{ $t('checkinList.individual.relationship') }}</span>
                <span class="wk-media-v">{{ relationship }}</span>
              </div>
            </div>
            <div class="wk-media-list">
              <div v-if="!hasMinorItem" class="wk-media-empty">{{ $t('checkinList.individual.noPhotos') }}</div>
              <div class="wk-media-item" v-if="minor?.minor_document_photo_url">
                <button type="button" class="wk-media-main" @click="openPreview(minor.minor_document_photo_url, $t('checkinList.individual.minorDocument'))">
                  <img class="wk-media-thumb" :src="minor.minor_document_photo_url" :alt="$t('checkinList.individual.minorDocument')" />
                  <div class="wk-media-body">
                    <div class="wk-media-name">{{ $t('checkinList.individual.document') }}</div>
                    <div class="wk-media-sub">{{ $t('checkinList.individual.minorDocument') }}</div>
                  </div>
                </button>
                <div class="wk-media-actions">
                  <button type="button" class="wk-icon-btn" @click="openPreview(minor.minor_document_photo_url, $t('checkinList.individual.minorDocument'))" :title="$t('checkinList.individual.view')"><i class="fa-regular fa-eye"></i></button>
                  <a class="wk-icon-btn" :href="minor.minor_document_photo_url" target="_blank" rel="noopener" :title="$t('checkinList.individual.download')"><i class="fa-solid fa-download"></i></a>
                </div>
              </div>
              <div class="wk-media-item" v-if="minor?.minor_authorization_photo_url">
                <button type="button" class="wk-media-main" @click="openPreview(minor.minor_authorization_photo_url, $t('checkinList.individual.minorAuthorization'))">
                  <img class="wk-media-thumb" :src="minor.minor_authorization_photo_url" :alt="$t('checkinList.individual.minorAuthorization')" />
                  <div class="wk-media-body">
                    <div class="wk-media-name">{{ $t('checkinList.individual.authorization') }}</div>
                    <div class="wk-media-sub">{{ $t('checkinList.individual.minorAuthorization') }}</div>
                  </div>
                </button>
                <div class="wk-media-actions">
                  <button type="button" class="wk-icon-btn" @click="openPreview(minor.minor_authorization_photo_url, $t('checkinList.individual.minorAuthorization'))" :title="$t('checkinList.individual.view')"><i class="fa-regular fa-eye"></i></button>
                  <a class="wk-icon-btn" :href="minor.minor_authorization_photo_url" target="_blank" rel="noopener" :title="$t('checkinList.individual.download')"><i class="fa-solid fa-download"></i></a>
                </div>
              </div>
            </div>
            <div class="wk-media-note" v-if="minor?.minor_relationship_other">
              <span class="wk-media-k">{{ $t('checkinList.individual.specification') }}</span>
              <span class="wk-media-v">{{ minor.minor_relationship_other }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── INFORMACIÓN ADICIONAL ─────────────────────────────────────────── -->
    <div class="wk-single__section">
      <div class="wk-single__title">{{ $t('checkinList.individual.additionalInfo') }}</div>

      <!-- Datos comunes: nacionalidad, nacimiento, género -->
      <div class="wk-card wk-card--soft">
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.nationality') }}</div>
          <div class="wk-kv__v">{{ nationalityLabel }}</div>
        </div>
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.birthDate') }}</div>
          <div class="wk-kv__v">
            {{ formatDateUTC(mostrar?.cliente?.date_of_birth) }}
            <span class="wk-muted">{{ $t('checkinList.individual.ageYears', { age: calcAgeFromUTC(mostrar?.cliente?.date_of_birth) }) }}</span>
          </div>
        </div>
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.gender') }}</div>
          <div class="wk-kv__v">
            <template v-if="mostrar?.cliente?.sex === 'F'">{{ $t('checkinList.individual.female') }}</template>
            <template v-else-if="mostrar?.cliente?.sex === 'M'">{{ $t('checkinList.individual.male') }}</template>
            <template v-else>{{ mostrar?.cliente?.sex || "—" }}</template>
          </div>
        </div>
      </div>

      <!-- ══════════════ ESPAÑA: SES / Ertzaintza / Mossos ══════════════ -->
      <div class="wk-card wk-card--soft wk-card--es" v-if="isVisibleData">
        <div class="wk-card__country-tag">{{ authority.flag }} {{ authority.label }}</div>
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.countryResidence') }}</div>
          <div class="wk-kv__v">{{ validateField(mostrar?.cliente?.country_residence) }}</div>
        </div>
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.cityResidence') }}</div>
          <div class="wk-kv__v">{{ validateField(mostrar?.cliente?.city_residence) }}</div>
        </div>
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.address') }}</div>
          <div class="wk-kv__v">{{ validateField(mostrar?.cliente?.address) }}</div>
        </div>
        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.zipCode') }}</div>
          <div class="wk-kv__v">{{ validateField(mostrar?.cliente?.zip_code) }}</div>
        </div>
        <div class="wk-kv__row" v-if="authority.key === 'ses_hospedaje'">
          <div class="wk-kv__k">{{ $t('checkinList.individual.sesBatchId') }}</div>
          <div class="wk-kv__v">{{ validateField(mostrar?.id_host) }}</div>
        </div>
      </div>

      <!-- ══════════════ ITALIA: Alloggiati Web ══════════════ -->
      <div class="wk-card wk-card--soft wk-card--italy" v-if="isItaly">
        <div class="wk-card__country-tag wk-card__country-tag--italy">🇮🇹 {{ $t('checkinList.individual.alloggiatiWeb') }}</div>

        <div class="wk-kv__row">
          <div class="wk-kv__k">{{ $t('checkinList.individual.countryBirth') }}</div>
          <div class="wk-kv__v">{{ paisNascitaLabel }}</div>
        </div>

        <div class="wk-kv__row" v-if="luogoRilascioLabel">
          <div class="wk-kv__k">{{ $t('checkinList.individual.docIssuePlace') }}</div>
          <div class="wk-kv__v">{{ luogoRilascioLabel }}</div>
        </div>
      </div>
    </div>

    <!-- ─── ACCIÓN ─────────────────────────────────────────────────────────── -->
    <div class="wk-actions">
      <a
        v-if="mostrar && mostrar.firma"
        class="wk-btn wk-btn--ghost"
        target="_blank"
        rel="noopener"
        :href="$base.server + mostrar.firma"
      >
        <i class="fa-regular fa-file-lines"></i>
        {{ $t('checkinList.individual.signedDocument') }}
      </a>
      <button type="button" class="wk-btn wk-btn--primary" @click="openEditDrawer">
        <i class="fa-regular fa-pen-to-square"></i>
        {{ $t('checkinList.individual.edit') }}
      </button>
    </div>

    <!-- ─── PREVIEW ────────────────────────────────────────────────────────── -->
    <div v-if="previewOpen" class="wk-preview" @click.self="closePreview">
      <div class="wk-preview__card">
        <div class="wk-preview__head">
          <div class="wk-preview__title">{{ previewTitle }}</div>
          <button type="button" class="wk-preview__close" @click="closePreview">
            <i class="fa-regular fa-times"></i>
          </button>
        </div>
        <div class="wk-preview__body">
          <img :src="previewUrl" :alt="$t('checkinList.individual.previewAlt')" />
        </div>
      </div>
    </div>

  </div>
</template>

<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&family=Inter:wght@400;500;600&display=swap');
</style>

<style lang="scss" scoped>
$font-main:  'DM Sans', -apple-system, sans-serif;
$font-meta:  'Inter', -apple-system, sans-serif;
$blue:   #2563eb;
$border: #e5e7eb;
$text:   #111827;
$muted:  #6b7280;
$radius: 14px;
$radius-s: 10px;

// ── Layout ──────────────────────────────────────────────────────────────────
.wk-single          { font-family: $font-main; display: flex; flex-direction: column; gap: 14px; }
.wk-single__section { display: flex; flex-direction: column; gap: 10px; }
.wk-single__title   { font-size: 13px; font-weight: 700; color: $muted; text-transform: uppercase; letter-spacing: .5px; font-family: $font-meta; }
.wk-single__name    { font-size: 17px; font-weight: 800; color: $text; }

// ── Cards ───────────────────────────────────────────────────────────────────
.wk-card {
  border: 1px solid $border; background: #fff;
  border-radius: $radius; padding: 14px;

  &--soft   { background: #f8fafc; border-color: #e6edf7; }

  &--es {
    border-color: #c7d2fe;
    background:   #f5f7ff;
  }
  &--italy {
    border-color: #c7d2fe;
    background:   #f5f7ff;
  }
}

.wk-card__country-tag {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: $font-meta;
  font-size: 11px; font-weight: 700;
  color: #3730a3; background: #eef2ff;
  border-radius: 999px; padding: 3px 10px;
  margin-bottom: 12px;

  &--italy { color: #3730a3; background: #eef2ff; }
}

// ── Key-value rows ──────────────────────────────────────────────────────────
.wk-kv__row {
  display: flex; justify-content: space-between;
  align-items: baseline; gap: 12px;
  padding: 5px 0; font-size: 13px;
  & + & { border-top: 1px solid #f3f4f6; }
}
.wk-kv__k { font-family: $font-meta; color: $muted; font-weight: 600; font-size: 12px; white-space: nowrap; }
.wk-kv__v { color: $text; font-weight: 700; text-align: right; }

.wk-muted  { color: $muted; font-weight: 500; font-size: 12px; font-family: $font-meta; margin-left: 4px; }
.wk-arrow  { color: #9ca3af; margin: 0 4px; }

// ── Badges Alloggiati ───────────────────────────────────────────────────────
.wk-badge {
  display: inline-block; border-radius: 999px;
  padding: 2px 10px;
  font-family: $font-meta; font-size: 11px; font-weight: 700;

  &.badge--ok      { background: #dcfce7; color: #166534; }
  &.badge--error   { background: #fee2e2; color: #991b1b; }
  &.badge--pending { background: #f3f4f6; color: #374151; }
}

// ── Acciones ────────────────────────────────────────────────────────────────
.wk-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 2px; }
.wk-btn {
  font-family: $font-main;
  border-radius: $radius-s; padding: 10px 16px;
  font-size: 13px; font-weight: 800;
  border: 1px solid transparent;
  display: inline-flex; gap: 8px; align-items: center; cursor: pointer;
}
.wk-btn--primary { background: $blue; border-color: $blue; color: #fff; }
.wk-btn--ghost   { background: #fff; border-color: #e5e7eb; color: #374151; }

// ── Media stack ─────────────────────────────────────────────────────────────
.wk-media-stack   { display: flex; flex-direction: column; gap: 10px; margin-top: 10px; }
.wk-media-section { border: none; padding: 0; background: transparent; }

.wk-media-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin-bottom: 8px;
  &--split { align-items: flex-start; }
}
.wk-media-title { font-size: 13px; font-weight: 800; color: $text; display: flex; gap: 8px; align-items: center; }
.wk-media-meta  { font-size: 12px; font-weight: 800; color: $muted; font-family: $font-meta; }
.wk-media-right { display: inline-flex; gap: 8px; align-items: baseline; }
.wk-media-k     { font-size: 12px; font-weight: 600; color: $muted; font-family: $font-meta; }
.wk-media-v     { font-size: 12px; font-weight: 700; color: $text; }

.wk-media-list  { display: flex; flex-direction: column; gap: 8px; }
.wk-media-item  {
  border: 1px solid #eef2f7; background: #f8fafc;
  border-radius: $radius-s; padding: 8px;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
}
.wk-media-main  {
  display: flex; align-items: center; gap: 10px;
  background: transparent; border: none; padding: 0;
  cursor: pointer; text-align: left; flex: 1;
}
.wk-media-thumb {
  width: 56px; height: 56px; border-radius: $radius-s;
  object-fit: cover; border: 1px solid $border; background: #fff; flex: 0 0 auto;
}
.wk-media-body  { display: flex; flex-direction: column; gap: 2px; }
.wk-media-name  { font-size: 13px; font-weight: 800; color: $text; }
.wk-media-sub   { font-family: $font-meta; font-size: 12px; font-weight: 500; color: $muted; }
.wk-media-actions { display: inline-flex; align-items: center; gap: 8px; flex: 0 0 auto; }
.wk-icon-btn {
  width: 34px; height: 34px; border-radius: $radius-s;
  border: 1px solid $border; background: #fff; color: $text;
  display: inline-flex; align-items: center; justify-content: center;
  text-decoration: none; cursor: pointer;
}
.wk-media-empty { font-family: $font-meta; font-size: 12px; font-weight: 600; color: $muted; padding: 8px 2px; }
.wk-media-note  {
  margin-top: 8px; display: flex; justify-content: space-between; gap: 12px;
  font-family: $font-meta; font-size: 12px; font-weight: 700;
  background: #f8fafc; border: 1px solid #eef2f7;
  border-radius: $radius-s; padding: 10px;
}

// ── Preview ──────────────────────────────────────────────────────────────────
.wk-preview        { position: fixed; inset: 0; background: rgba(17,24,39,.55); display: flex; align-items: center; justify-content: center; padding: 16px; z-index: 99999; }
.wk-preview__card  { width: min(820px, 100%); background: #fff; border-radius: 16px; border: 1px solid $border; overflow: hidden; }
.wk-preview__head  { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border-bottom: 1px solid #eef2f7; }
.wk-preview__title { font-size: 14px; font-weight: 800; color: $text; }
.wk-preview__close { width: 36px; height: 36px; border-radius: $radius-s; border: 1px solid $border; background: #fff; color: $text; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.wk-preview__body  { padding: 12px; }
.wk-preview__body img { width: 100%; height: auto; border-radius: $radius-s; border: 1px solid $border; }
</style>
